import {
  ApiResponse,
  IMeProfile,
  INguoiDungProfileUpdatePayload,
  IUserResponse,
} from '@/core/interfaces/ApiResponses';
import { apiServiceInstance, getUserFromLocalStorage } from '@/core/helpers/utils';
import store from '@/store';
import { Mutations } from '@/store/enums/StoreEnums';

/**
 * Self-service profile APIs (`/api/v1/me/profile*`).
 * Prefer these over `/auth/me` and `/nguoi-dung/*` for account settings screens.
 */
export class ProfileService {
  static getProfile(): Promise<{
    data: ApiResponse<IMeProfile>;
  }> {
    return apiServiceInstance().vueInstance.axios.get('/api/v1/me/profile');
  }

  static updateProfile(payload: INguoiDungProfileUpdatePayload): Promise<{
    data: ApiResponse<IMeProfile>;
  }> {
    return apiServiceInstance().vueInstance.axios.post('/api/v1/me/profile', {
      profileInstance: payload,
    });
  }

  static uploadAvatar(file: File): Promise<{
    data: ApiResponse<IMeProfile>;
  }> {
    const formData = new FormData();
    formData.append('anh_dai_dien', file);

    return apiServiceInstance().vueInstance.axios.post(
      '/api/v1/me/profile/avatar',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
  }

  /**
   * Merge profile fields into the auth store so Topbar / UserMenu stay in sync.
   * Preserves session-only fields (permissions, password hashes, timestamps).
   */
  static applyProfileToStore(profile: IMeProfile): void {
    const stored = (getUserFromLocalStorage() || {}) as Partial<IUserResponse>;
    const current = store.getters.currentUser || {};

    store.commit(Mutations.SET_USER, {
      ...stored,
      id: profile.id,
      ho_va_ten: profile.ho_va_ten,
      ten_dang_nhap: profile.ten_dang_nhap,
      email: profile.email ?? '',
      so_dien_thoai: profile.so_dien_thoai,
      // Keep relative DB path + absolute URL separate for Topbar resolution
      anh_dai_dien: profile.anh_dai_dien || stored.anh_dai_dien || '',
      anh_dai_dien_url:
        profile.anh_dai_dien_url || stored.anh_dai_dien_url || undefined,
      id_chuc_vu: profile.id_chuc_vu,
      id_bo_phan: profile.id_bo_phan,
      id_don_vi: profile.id_don_vi,
      cap_don_vi:
        profile.cap_don_vi ??
        profile.don_vi?.cap_don_vi ??
        stored.cap_don_vi ??
        current.capDonVi,
      ma_don_vi:
        profile.don_vi?.ma_don_vi || stored.ma_don_vi || current.madonvi || '',
      chuc_vu: profile.chuc_vu || undefined,
      bo_phan: profile.bo_phan || undefined,
      don_vi: profile.don_vi
        ? {
            id: profile.don_vi.id,
            ten_don_vi: profile.don_vi.ten_don_vi || '',
            ma_don_vi: profile.don_vi.ma_don_vi || '',
            cap_don_vi: profile.don_vi.cap_don_vi,
            ten_don_vi_cha: profile.don_vi.ten_don_vi_cha,
          }
        : undefined,
      permissions: stored.permissions || current.permissions || [],
      roles: stored.roles || current.roles || [],
      mat_khau: stored.mat_khau || current.password || '',
      mat_khau_md5: stored.mat_khau_md5 || current.encodePassword || '',
      email_verified_at: stored.email_verified_at || current.emailVerifiedAt || '',
      created_at: stored.created_at || current.createdAt || '',
      updated_at: stored.updated_at || current.updatedAt || '',
      deleted_at: stored.deleted_at || current.deletedAt || '',
    } as IUserResponse);
  }
}
